import { useContext, useState, useEffect } from "react";
import Checkbox from "@mui/material/Checkbox";

import ModalTemplate from "../../../../../components/modal/modalTemplate";
import { Context } from "../../../Provider";
import { theme } from "../../../../../theme";
import { commonSwal } from "../../../../../components/swalAlert/swalAlert";
import { updateFtmSkills } from "../../../../components/addFtmSkills/service";

const label = { inputProps: { "aria-label": "Checkbox demo" } };

const EmptyState = () => (
    <div
        style={{ height: `${window.innerHeight - 387}px` }}
        className="d-flex justify-content-center align-items-center"
    >
        <h5>No service categories and types found.</h5>
    </div>
);

const CategoryCard = ({ category, onSelectionChange, ftm_skills }) => {
    // ✅ Find pre-selected subSkillIds from ftm_skills for this category
    const preSelectedSubIds =
        ftm_skills
            ?.find((fs) => fs.skill_id === category.skill_id)
            ?.subSkills?.map((s) => s.sub_skill_id) || [];

    // ✅ Initialize checkedSubs based on ftm_skills
    const [checkedSubs, setCheckedSubs] = useState(
        category.sub_skills?.map((sub) => preSelectedSubIds.includes(sub.sub_skill_id)) || []
    );

    const [categoryChecked, setCategoryChecked] = useState(
        checkedSubs.length > 0 && checkedSubs.every(Boolean)
    );

    // ✅ On mount, sync parent with already selected subs
    useEffect(() => {
        category.sub_skills?.forEach((sub, index) => {
            if (checkedSubs[index]) {
                onSelectionChange(category.skill_id, sub.sub_skill_id, true);
            }
        });
    }, []); // run once

    // ✅ Update category checkbox when sub checkboxes change
    useEffect(() => {
        if (checkedSubs.length > 0) {
            setCategoryChecked(checkedSubs.every(Boolean));
        }
    }, [checkedSubs]);

    const handleCategoryChange = (e) => {
        const checked = e.target.checked;
        setCategoryChecked(checked);
        const updated = checkedSubs.map(() => checked);
        setCheckedSubs(updated);

        // notify parent for all subs
        category.sub_skills.forEach((sub) => {
            onSelectionChange(category.skill_id, sub.sub_skill_id, checked);
        });
    };

    const handleSubChange = (index, e) => {
        const updated = [...checkedSubs];
        updated[index] = e.target.checked;
        setCheckedSubs(updated);

        // notify parent
        const subSkillId = category.sub_skills[index].sub_skill_id;
        onSelectionChange(category.skill_id, subSkillId, e.target.checked);
    };

    return (
        <div className="card mb-4" style={{ boxShadow: "unset" }}>
            <div className="card-header pb-0 p-3">
                <div className="row align-items-center mb-1">
                    <div className="col-1">
                        <i className={`material-icons text-${theme.main} mt-2`}>
                            {category.skill_web_app_icon}
                        </i>
                    </div>
                    <div className="col-11 ps-0 d-flex px-2">
                        <h6 className="mb-1 text-dark text-md mt-2">
                            {category.skill_name}
                        </h6>
                        <div className="ms-auto">
                            <Checkbox
                                {...label}
                                color="success"
                                checked={categoryChecked}
                                onChange={handleCategoryChange}
                                style={{ marginRight: "12px" }}
                                size="small"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {category.sub_skills?.length > 0 && (
                <div className="card-body p-3 py-0">
                    <ul className="list-unstyled mx-auto">
                        {category.sub_skills.map((sub, index) => (
                            <li
                                key={sub.sub_skill_id ?? sub.sub_skill_name}
                                className="d-flex px-2"
                            >
                                <h6 className="text-dark fw-bold text-md mt-2">
                                    {sub.sub_skill_name}
                                </h6>
                                <div className="ms-auto">
                                    <Checkbox
                                        {...label}
                                        color="success"
                                        checked={checkedSubs[index]}
                                        onChange={(e) => handleSubChange(index, e)}
                                        size="small"
                                    />
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

const AddNewServiceCategoriesAndTypes = ({ show, handleClose }) => {
    const { state, dispatch } = useContext(Context);
    const { ftm, skills } = state;
    const { ftm_id, ftm_skills } = ftm;

    // ✅ Initialize selectedSubSkills from ftm_skills
    const [selectedSubSkills, setSelectedSubSkills] = useState(() =>
        ftm_skills.flatMap((fs) => fs.subSkills?.map((s) => s.sub_skill_id) || [])
    );

    const handleSelectionChange = (categoryId, subSkillId, checked) => {
        setSelectedSubSkills((prev) => {
            if (checked) {
                return [...new Set([...prev, subSkillId])]; // add unique
            } else {
                return prev.filter((id) => id !== subSkillId); // remove
            }
        });
    };

    const handleSave = () => {
        // 👉 send selectedSubSkills to API or handle as needed

        commonSwal({
            notificationType: 'UPDATE_FTM_SKILLS_AND_CERTIFICATES',
            isConfirmed: async (result) => {
                const transform_skills = [];
                skills.forEach(skill => {
                    skill.sub_skills.forEach(sub_skill => {
                        transform_skills.push({
                            skill_id: sub_skill.sub_skill_id,  // Use sub_skill_id as skill_id in the result
                            status: new Set(selectedSubSkills).has(sub_skill.sub_skill_id) ? 1 : 0
                        });
                    });
                });
                
                await updateFtmSkills({
                    ftm_id: ftm_id,
                    skills: transform_skills
                });

                dispatch({ type: "SET_FTM_EDIT_COUNT" });
            }
        })
    };

    return (
        <ModalTemplate
            show={show}
            closeFun={handleClose}
            size={null}
            centered
            title="Add new service categories & types"
            body={
                <section>
                    {skills.length === 0 ? (
                        <EmptyState />
                    ) : (
                        skills
                            .filter((category) => category.sub_skills?.length > 0)
                            .map((category) => (
                                <CategoryCard
                                    key={category.skill_id ?? category.skill_name}
                                    category={category}
                                    onSelectionChange={handleSelectionChange}
                                    ftm_skills={ftm_skills}
                                />
                            ))
                    )}
                </section>
            }
            footer={
                <>
                    <button
                        className={`btn btn-icon btn-3 btn-${theme.close_btn} btn-sm`}
                        type="button"
                        onClick={handleClose}
                    >
                        <span className="btn-inner--icon">
                            <i className="material-icons text-lg">close</i>
                        </span>
                        <span className="btn-inner--text ms-1">Close</span>
                    </button>
                    <button
                        className={`btn btn-icon btn-3 btn-${theme.main} btn-sm`}
                        type="button"
                        onClick={handleSave}
                    >
                        <span className="btn-inner--icon">
                            <i className="material-icons text-lg">save</i>
                        </span>
                        <span className="btn-inner--text ms-1">Save</span>
                    </button>
                </>
            }
        />
    );
};

export default AddNewServiceCategoriesAndTypes;
